/**
 * Assessments API client — Stories 2.2–2.8.
 *
 * Thin fetch helpers for the endpoints delivered by Story 2.1
 * (`apps/api/src/routes/assessments.ts`). All requests include
 * cookies (better-auth session) and accept JSON; non-2xx responses
 * surface as thrown `Error` instances so TanStack Query / mutations
 * route them through the standard error-state pipeline.
 *
 * Source of truth for the row shape lives in `packages/db/postgres/schema/domain.ts`
 * (table `assessments`). The shape here mirrors what the GET endpoint
 * returns; it is intentionally local (no DB import from the SPA bundle).
 *
 * Sections:
 *  - List / mutate (Story 2.2)
 *  - Create / read / update metadata (Story 2.3)
 *  - Topics CRUD + reorder (Story 2.4)
 *  - Questions CRUD + reorder (Story 2.5)
 *  - Rubric typing on questions (Story 2.6)
 *  - Export / import JSON snapshot (Story 2.8)
 *
 * ⚠️ LÁPIDE — o cliente do GLOSSÁRIO ASR (Story 2.7 + 2.7.1) saiu daqui a
 * 2026-09-05, atrás da tela que o servia (`asr-glossary-editor.tsx`, removida
 * antes). Ficam registados os nomes por que se procura: `AsrEntry`,
 * `createAsrEntry`, `deleteAsrEntry`, `updateAsrEntry`, `listAsrGlossary` e
 * `AssessmentWithExpansions`. O legado do auto-detect por keyword já tinha
 * saído no Epic 2.5 / Story 2.5.1.
 *
 * ⚠️ E O SERVIDOR SAIU NA MESMA LEVA. As rotas `POST/PATCH/DELETE
 * /:id/asr-glossary` foram removidas do Worker, `expandInterview` deixou de ler
 * a tabela e por isso `GET /:id` já não devolve `asrGlossary` — nem o
 * `POST /:id/duplicate` a copia. O que sobrevive mais um ciclo é só a TABELA
 * `interview_asr_glossary` no D1: o deploy aplica migrations ANTES de publicar
 * o Worker, e largá-la junto com o código daria `no such table` ao Worker ainda
 * em serviço. Ver o cabeçalho de `apps/api/src/routes/assessments.ts`.
 */
import type { BehavioralConfig, GradingConfig, RubricLevel } from '@caliber/shared/dtos'
import { API_BASE } from '@/lib/api'
import { parseProblemJson, readProblemMessage } from '@/lib/api/problem'
import type { InterviewQuestion, InterviewTopic } from '@caliber/shared/dtos'

// ---------------------------------------------------------------
// Types — mirror packages/db/postgres/schema/domain.ts (assessments table)
// ---------------------------------------------------------------

export type AssessmentType = 'practical' | 'interview'

export interface Assessment {
  id: string
  slug: string
  name: string
  type: AssessmentType
  description: string | null
  enabled: boolean
  /**
   * Story 2.15 — full extracted text of an imported job-posting document.
   * `null` when the assessment was not created/updated via document import.
   */
  jobDescriptionSource: string | null
  /**
   * Story 13.2 (Epic 13) — enunciado da prova. Todos `null` quando ausentes.
   * `statementText`: Markdown do enunciado (renderizado inline na página de
   * detalhe). `statementFileR2Key`/`statementFileName`: arquivo PDF/DOCX
   * anexado (chave R2 + nome original) — o download é feito por
   * `getAssessmentStatement`. Práticas sem enunciado têm os 3 `null`.
   */
  statementText: string | null
  statementFileR2Key: string | null
  statementFileName: string | null
  /**
   * Story 13.3 (Epic 13) — config de avaliação editável por prova prática.
   * `null` quando não configurado. O GET full-row já devolve um OBJETO (a coluna
   * Drizzle está em `mode:'json'`), então não há parse no cliente. Só
   * `subjectiveInstructions` é editável/consumido nesta story.
   */
  gradingConfig: GradingConfig | null
  /**
   * Tamanho do roteiro de uma ENTREVISTA — exibido na aba "Entrevistas" do
   * catálogo. Devolvidos por `GET /api/assessments`; para uma prova prática são
   * sempre 0 (as tabelas `interview_*` não têm linhas dela), e por isso a aba de
   * provas não os mostra. OPCIONAIS: outros pontos que produzem um `Assessment`
   * (detalhe, criação, fixtures) não os carregam.
   */
  topicCount?: number
  questionCount?: number
  createdBy: string | null
  /** ISO-8601 timestamp string serialised by Hono `c.json`. */
  createdAt: string
  /** ISO-8601 timestamp string serialised by Hono `c.json`. */
  updatedAt: string
}

/** Response envelope for `GET /api/assessments` (Story 2.1). */
export interface AssessmentsListResponse {
  items: Assessment[]
  page: number
  pageSize: number
  total: number
}

// ---------------------------------------------------------------
// List filters (URL ↔ backend query-string mapping)
// ---------------------------------------------------------------

export type TypeFilter = 'all' | 'practical' | 'interview'
export type EnabledFilter = 'active' | 'disabled' | 'all'

export interface ListAssessmentsParams {
  /** Backend filter — when 'all' the param is omitted. */
  type?: TypeFilter
  /**
   * UI filter mapped to backend:
   *   active   → ?enabled=true   (default)
   *   disabled → ?enabled=false
   *   all      → omit
   */
  enabled?: EnabledFilter
  /** 1-indexed. */
  page?: number
  /** Default 20 (story spec); backend caps at 200. */
  pageSize?: number
  /** Cookie credentials. */
  signal?: AbortSignal
}

function buildListUrl(params: ListAssessmentsParams): string {
  const url = new URL(`${API_BASE}/assessments`, window.location.origin)
  const { type = 'all', enabled = 'active', page = 1, pageSize = 20 } = params

  if (type !== 'all') url.searchParams.set('type', type)
  if (enabled === 'active') url.searchParams.set('enabled', 'true')
  else if (enabled === 'disabled') url.searchParams.set('enabled', 'false')
  // enabled='all' → omit param
  url.searchParams.set('page', String(page))
  url.searchParams.set('pageSize', String(pageSize))

  // Return a path-relative URL so the dev Vite proxy + prod Pages routing
  // both apply (`/api/...` not `http://host/api/...`).
  return `${url.pathname}${url.search}`
}

export { buildListUrl as __buildListUrlForTests }

/**
 * GET /api/assessments — paginated list with optional filters.
 * Throws on non-2xx (including 4xx — caller should handle in mutation/query error).
 */
export async function listAssessments(
  params: ListAssessmentsParams = {},
): Promise<AssessmentsListResponse> {
  // exactOptionalPropertyTypes: only attach `signal` when present — DOM types
  // model RequestInit.signal as `AbortSignal | null`, not `... | undefined`.
  const init: RequestInit = {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }
  if (params.signal) init.signal = params.signal

  const res = await fetch(buildListUrl(params), init)
  if (!res.ok) {
    throw new Error(`Failed to list assessments (${res.status})`)
  }
  return (await res.json()) as AssessmentsListResponse
}

// ---------------------------------------------------------------
// Read-only criteria rubric — Story 13.1 (Epic 13)
//
// `GET /api/assessments/:id/criteria` serves the static rubric of a PRACTICAL
// assessment (source of truth: apps/api/src/lib/practical-criteria.ts). The
// criteria represent the prova AS DESIGNED (fiel ao .md, Article IV), not a
// mirror of what the runner executes. 404 only for interview assessments.
// ---------------------------------------------------------------

/** One read-only rubric criterion — mirrors `CriteriaItem` from the API. */
export interface CriteriaItem {
  checkNumber: string
  description: string
  weight: number
  phase: string
}

/**
 * GET /api/assessments/:id/criteria — Story 13.1 / AC-6.
 *
 * Returns the practical rubric criteria (possibly `[]` for a practical with no
 * criteria defined yet). Throws on non-2xx (incl. 404 when the assessment is an
 * interview or does not exist) so TanStack Query routes it through its error
 * pipeline — the caller treats a 404/error gracefully (no rubric section).
 */
export async function getAssessmentCriteria(
  id: string,
  signal?: AbortSignal,
): Promise<CriteriaItem[]> {
  const init: RequestInit = {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }
  if (signal) init.signal = signal

  const res = await fetch(`${API_BASE}/assessments/${encodeURIComponent(id)}/criteria`, init)
  if (!res.ok) {
    throw new Error(`Failed to load criteria (${res.status})`)
  }
  const body = (await res.json()) as { criteria: CriteriaItem[] }
  return body.criteria
}

// ---------------------------------------------------------------
// Statement (enunciado) download — Story 13.2 (Epic 13)
//
// `GET /api/assessments/:id/statement` retorna uma URL pré-assinada (15 min)
// para o arquivo PDF/DOCX vinculado. O botão "Baixar enunciado" abre essa URL
// em nova aba. 404 quando não há arquivo vinculado.
// ---------------------------------------------------------------

/** Resposta de `GET /api/assessments/:id/statement`. */
export interface StatementDownload {
  url: string
  name: string | null
}

/**
 * GET /api/assessments/:id/statement — Story 13.2 / AC-7.
 * Lança em não-2xx (incl. 404 quando não há arquivo) para a mutation/query
 * roteá-lo pelo pipeline de erro padrão.
 */
export async function getAssessmentStatement(id: string): Promise<StatementDownload> {
  const res = await fetch(`${API_BASE}/assessments/${encodeURIComponent(id)}/statement`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new Error(`Failed to load statement (${res.status})`)
  }
  return (await res.json()) as StatementDownload
}

/** MIME do `.docx` (OOXML). Mesma constante de `lib/api/professionals.ts`, outro módulo. */
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

/**
 * Teto do lado do cliente — espelha `PRESIGN_MAX_SIZE_BYTES['assessment-statement']`
 * (20 MiB). Validar aqui não substitui o servidor: poupa um PUT que morreria com
 * um 413 DEPOIS de subir, e diz o motivo em português antes da espera.
 */
export const STATEMENT_MAX_BYTES = 20 * 1024 * 1024

/**
 * `accept` do seletor. MIME **e** extensão, pela mesma razão de
 * `PROFESSIONAL_CV_ACCEPT`: há sistemas que reportam `application/octet-stream` para
 * um `.docx`, e só a extensão o salva do filtro. Isto filtra apenas o PICKER.
 */
export const STATEMENT_ACCEPT = ['application/pdf', DOCX_MIME, '.pdf', '.docx'].join(',')

/**
 * O content-type que vai no presign **e** no PUT — a assinatura do R2 liga os
 * dois, por isso tem de ser a MESMA string nas duas chamadas, e uma das duas do
 * enum fechado do servidor. Deriva do MIME com a extensão como rede (mesma ordem
 * de `professionalCvKind`), porque `file.type` de um `.docx` varia com o sistema.
 * `null` = formato não aceito.
 */
export function statementContentType(file: File): string | null {
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()
  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'application/pdf'
  if (type === DOCX_MIME || name.endsWith('.docx')) return DOCX_MIME
  return null
}

/**
 * Valida ANTES de enviar; devolve a frase pt-BR pronta, ou `null` quando passa.
 * O formato vem primeiro de propósito: um `.exe` de 30 MB deve ouvir "formato não
 * aceito", não "arquivo grande demais" — a segunda frase sugere que um `.exe`
 * menor serviria.
 */
export function validateStatementFile(file: File): string | null {
  if (!statementContentType(file)) return 'Formato não aceito. Envie um arquivo PDF ou DOCX.'
  if (file.size > STATEMENT_MAX_BYTES) return 'Arquivo grande demais. O limite é 20 MB.'
  return null
}

/** O que `POST /api/assessments/:id/statement` devolve (slice do assessment). */
export interface AttachedStatement {
  statementFileR2Key: string
  statementFileName: string
}

/**
 * POST /api/assessments/:id/statement — vincula ao modelo um arquivo já enviado
 * ao R2 pelo PUT pré-assinado (`purpose: 'assessment-statement'`).
 *
 * NÃO é admin-only: vincular o enunciado de uma prova é a única mutação de
 * `/api/assessments/*` aberta ao recruiter (exceção explícita do owner à matriz
 * de papéis). O servidor continua a recusar o resto.
 *
 * Erros vêm em RFC 7807 e a frase útil está em `detail` (o padrão dominante
 * deste worker) — daí `readProblemMessage` com o campo default, em vez de um
 * `Failed to …(status)` que o usuário não pode ler. É o que faz um 403 de
 * "enunciado só existe para prova prática" chegar à tela com essas palavras.
 */
export async function attachAssessmentStatement(
  id: string,
  body: { r2Key: string; fileName: string },
): Promise<AttachedStatement> {
  const res = await fetch(`${API_BASE}/assessments/${encodeURIComponent(id)}/statement`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Não foi possível vincular o enunciado (${res.status}).`),
    )
  }
  const data = (await res.json()) as { assessment: AttachedStatement }
  return data.assessment
}

/**
 * PATCH /api/assessments/:id/grading-config — Story 13.3 / AC-4 + AC-9.
 *
 * Replace COMPLETO da config de avaliação (não merge). Nesta story a UI envia só
 * `{ subjectiveInstructions }` (os params 13.4 ainda não têm UI — débito
 * DEBT-13.3-UI-PARTIAL: a 13.4 deve enviar `{ ...existingConfig, ... }`). Admin-only
 * no servidor (`requireAdmin`); lança em não-2xx. Retorna a config gravada.
 */
export async function patchGradingConfig(
  id: string,
  config: GradingConfig,
): Promise<GradingConfig> {
  const res = await fetch(`${API_BASE}/assessments/${encodeURIComponent(id)}/grading-config`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(config),
  })
  if (!res.ok) {
    throw new Error(`Failed to save grading config (${res.status})`)
  }
  const data = (await res.json()) as { assessment: { id: string; gradingConfig: GradingConfig } }
  return data.assessment.gradingConfig
}

// ---------------------------------------------------------------
// Mutations — PATCH for enable/disable, DELETE for hard-delete
// ---------------------------------------------------------------

/**
 * PATCH /api/assessments/:id — partial update.
 * Returns the updated assessment row.
 */
export async function patchAssessment(
  id: string,
  body: { enabled?: boolean; name?: string; description?: string | null },
): Promise<Assessment> {
  const res = await fetch(`${API_BASE}/assessments/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    throw new Error(`Failed to update assessment (${res.status})`)
  }
  return (await res.json()) as Assessment
}

/** O que impede um modelo de ser apagado — as contagens que o 200 devolve. */
export interface AssessmentLinks {
  evaluations: number
  opportunities: number
  evaluationChecks: number
}

/**
 * DELETE /api/assessments/:id.
 *
 * A rota tem dois desfechos, e os dois são 2xx:
 *   • 204 — o modelo não tinha vínculo nenhum e foi mesmo excluído;
 *   • 200 com `mode: 'soft-disable'` — havia vínculo, então ele foi DESATIVADO,
 *     e o corpo traz as contagens do que bloqueou.
 *
 * Este cliente lia `res.status === 409` para detectar a conversão — um 409 que
 * a rota NUNCA devolveu (o soft-disable sempre foi 200). Resultado: um modelo
 * apenas desativado saía por `res.ok` e a tela dizia "Modelo excluído.". A
 * verdade vem do corpo, e é ele que se lê agora.
 */
export async function deleteAssessment(
  id: string,
): Promise<{ converted: boolean; links: AssessmentLinks | null }> {
  const res = await fetch(`${API_BASE}/assessments/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new Error(`Failed to delete assessment (${res.status})`)
  }
  // 204 — exclusão real, sem corpo.
  if (res.status === 204) return { converted: false, links: null }

  const body = (await res.json().catch(() => null)) as {
    mode?: string
    links?: Partial<AssessmentLinks>
  } | null
  if (body?.mode !== 'soft-disable') return { converted: false, links: null }
  return {
    converted: true,
    links: {
      evaluations: body.links?.evaluations ?? 0,
      opportunities: body.links?.opportunities ?? 0,
      evaluationChecks: body.links?.evaluationChecks ?? 0,
    },
  }
}

// ---------------------------------------------------------------
// Create / read / update — Story 2.3 (metadata form)
// ---------------------------------------------------------------

/**
 * Structured API error — preserves HTTP status + parsed problem-json
 * payload so the form can map field-level Zod issues into RHF
 * `setError(field, { message })` and surface 409 as a slug conflict.
 *
 * The Worker (Story 2.1) returns either:
 *   - `{ error: string, code?: string, details?: { fieldErrors?: Record<string,string[]> } }`
 *   - `{ error: { message, code, details } }` (alt shape — tolerated)
 */
export interface ApiErrorPayload {
  error?: string | { message?: string; code?: string }
  code?: string
  details?: { fieldErrors?: Record<string, string[]>; formErrors?: string[] }
  [key: string]: unknown
}

export class ApiError extends Error {
  readonly status: number
  readonly payload: ApiErrorPayload | null

  constructor(message: string, status: number, payload: ApiErrorPayload | null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.payload = payload
  }

  /** True when the backend signals a slug uniqueness conflict (HTTP 409 or 400 with `slug_taken`). */
  get isSlugConflict(): boolean {
    if (this.status === 409) return true
    const code =
      this.payload?.code ??
      (typeof this.payload?.error === 'object' ? this.payload.error?.code : undefined)
    return code === 'slug_taken'
  }

  /** Zod field-level errors (when the backend echoes them in `details.fieldErrors`). */
  get fieldErrors(): Record<string, string[]> | null {
    return this.payload?.details?.fieldErrors ?? null
  }
}

/**
 * Read the response body once and best-effort parse JSON; an empty body or
 * non-JSON response collapses to `null` so callers always get a value.
 */
async function safeJson(res: Response): Promise<ApiErrorPayload | null> {
  try {
    const text = await res.text()
    if (text.length === 0) return null
    return JSON.parse(text) as ApiErrorPayload
  } catch {
    return null
  }
}

function errorMessage(payload: ApiErrorPayload | null, fallback: string): string {
  if (!payload) return fallback
  if (typeof payload.error === 'string') return payload.error
  if (typeof payload.error === 'object' && payload.error?.message) return payload.error.message
  // Um problem+json (o 403 do portão de admin, desde o #498) não traz `error`.
  if (typeof payload.detail === 'string' && payload.detail.length > 0) return payload.detail
  return fallback
}

/**
 * Body accepted by {@link createAssessment}. Mirrors
 * `CreateAssessmentSchema` from `@caliber/shared` plus the hardcoded
 * `type: 'interview'` discriminator (Story 2.3 scope: interviews only —
 * `type` is not a form field).
 */
export interface CreateAssessmentBody {
  name: string
  slug?: string
  description?: string
  type: 'interview'
  /** Story 2.15 — full extracted text of an imported job-posting document. */
  jobDescriptionSource?: string
}

/**
 * Body accepted by {@link updateAssessment}. Mirrors `UpdateAssessmentSchema`
 * (`packages/shared/src/dtos.ts:112`) — partial, `.strict()`, requires at
 * least one field. The web form only ever sends the fields it manages.
 */
export interface UpdateAssessmentBody {
  name?: string
  description?: string | null
  slug?: string
  enabled?: boolean
  /** Story 2.15 — updated job-posting source text (`null` clears it). */
  jobDescriptionSource?: string | null
}

/**
 * GET /api/assessments/:idOrSlug — fetch a single assessment by UUID or slug.
 * 404 surfaces as an {@link ApiError} so the editor route can render an empty state.
 */
export async function fetchAssessment(
  idOrSlug: string,
  options: { signal?: AbortSignal } = {},
): Promise<Assessment> {
  const init: RequestInit = {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }
  if (options.signal) init.signal = options.signal

  const res = await fetch(`${API_BASE}/assessments/${encodeURIComponent(idOrSlug)}`, init)
  if (!res.ok) {
    const payload = await safeJson(res)
    throw new ApiError(
      errorMessage(payload, `Failed to fetch assessment (${res.status})`),
      res.status,
      payload,
    )
  }
  return (await res.json()) as Assessment
}

/**
 * POST /api/assessments — create a new assessment row.
 * Story 2.3 AC4 / AC5. Returns the persisted row including server-assigned
 * `id`, `createdAt`, `updatedAt`.
 */
export async function createAssessment(body: CreateAssessmentBody): Promise<Assessment> {
  const res = await fetch(`${API_BASE}/assessments`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const payload = await safeJson(res)
    throw new ApiError(
      errorMessage(payload, `Failed to create assessment (${res.status})`),
      res.status,
      payload,
    )
  }
  return (await res.json()) as Assessment
}

/**
 * PATCH /api/assessments/:id — partial update of the metadata fields the
 * form manages. Story 2.3 AC4 / AC6.
 *
 * Distinct from {@link patchAssessment}, which is a narrower helper kept
 * for the row-level enable/disable toggle in Story 2.2's list page.
 */
export async function updateAssessment(
  id: string,
  body: UpdateAssessmentBody,
): Promise<Assessment> {
  const res = await fetch(`${API_BASE}/assessments/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const payload = await safeJson(res)
    throw new ApiError(
      errorMessage(payload, `Failed to update assessment (${res.status})`),
      res.status,
      payload,
    )
  }
  return (await res.json()) as Assessment
}

// ---------------------------------------------------------------
// Job-document extraction — Story 2.15
//
// `POST /api/assessments/extract` takes the text the client already
// extracted from a job-posting document (PDF/.docx/.md/.txt) and asks the
// LLM to derive interview metadata. The slug is NOT returned — callers
// derive it from `name` with `slugify` from `@/lib/slug`.
// ---------------------------------------------------------------

/** Shape returned by `POST /api/assessments/extract` (Story 2.15 AC3). */
export interface ExtractedJobMetadata {
  name: string
  description: string
}

/**
 * POST /api/assessments/extract — Story 2.15 / AC3.
 *
 * Sends the extracted document text to the LLM-backed endpoint. On non-2xx
 * the call throws an {@link ApiError} so the metadata form can surface an
 * inline error without modifying the user's current field values (AC6).
 *
 * The backend caps `text` at 50 000 chars (413). Callers that extracted a
 * larger document should expect the {@link ApiError} with `status === 413`.
 */
export async function extractFromDocument(text: string): Promise<ExtractedJobMetadata> {
  const res = await fetch(`${API_BASE}/assessments/extract`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ text }),
  })
  if (!res.ok) {
    const payload = await safeJson(res)
    throw new ApiError(
      errorMessage(payload, `Failed to extract job document (${res.status})`),
      res.status,
      payload,
    )
  }
  return (await res.json()) as ExtractedJobMetadata
}

// ============================================================
// Topics CRUD + reorder — Story 2.4
//   + questions CRUD + reorder — Story 2.5
// Spec: caliber-web/docs/architecture.md §7.2 (endpoints)
//
// All endpoints are mounted under /api (proxied to the Worker in
// dev per vite.config.ts; routed by _routes.json in production).
// ============================================================

export interface CreateTopicInput {
  name: string
  weight: number
  order: number
}

export interface UpdateTopicInput {
  name?: string
  weight?: number
  order?: number
}

export interface ReorderTopicItem {
  id: string
  order: number
}

class AssessmentsApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'AssessmentsApiError'
    this.status = status
  }
}

async function parseOrThrow<T>(res: Response, operation: string): Promise<T> {
  if (!res.ok) {
    let detail = ''
    try {
      const body = await res.json()
      detail =
        typeof body === 'object' && body && 'error' in body
          ? String((body as { error: unknown }).error)
          : ''
    } catch {
      /* body wasn't JSON — keep detail empty */
    }
    throw new AssessmentsApiError(
      res.status,
      `Failed to ${operation}: ${res.status}${detail ? ` (${detail})` : ''}`,
    )
  }
  // 204 No Content for DELETE
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export async function createTopic(
  assessmentId: string,
  input: CreateTopicInput,
): Promise<InterviewTopic> {
  const res = await fetch(`${API_BASE}/assessments/${assessmentId}/topics`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  })
  return parseOrThrow<InterviewTopic>(res, 'create topic')
}

export async function updateTopic(
  assessmentId: string,
  topicId: string,
  input: UpdateTopicInput,
): Promise<InterviewTopic> {
  const res = await fetch(`${API_BASE}/assessments/${assessmentId}/topics/${topicId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  })
  return parseOrThrow<InterviewTopic>(res, 'update topic')
}

/**
 * O 409 de "isto já avaliou alguém" — tipado porque a interface tem de AGIR
 * sobre ele (o diálogo troca de estado e explica), não só mostrá-lo num toast.
 * Mesmo desenho do `CustomerLinkedError` em `lib/api/customers.ts`: a classe
 * carrega o dado estruturado do corpo, e a frase vem do servidor porque é lá
 * que ela é escrita uma vez só.
 */
export class AlreadyUsedInEvaluationError extends Error {
  readonly evaluationChecks: number

  constructor(init: { message: string; evaluationChecks: number }) {
    super(init.message)
    this.name = 'AlreadyUsedInEvaluationError'
    this.evaluationChecks = init.evaluationChecks
  }
}

/** Lê a contagem de checks do problem+json, tolerando corpo ilegível. */
async function readAlreadyUsed(
  res: Response,
  fallback: string,
): Promise<AlreadyUsedInEvaluationError> {
  const body = await parseProblemJson(res)
  const raw = body?.evaluationChecks
  const message =
    typeof body?.detail === 'string' && body.detail.length > 0 ? body.detail : fallback
  return new AlreadyUsedInEvaluationError({
    message,
    evaluationChecks: typeof raw === 'number' ? raw : 0,
  })
}

export async function deleteTopic(assessmentId: string, topicId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/assessments/${assessmentId}/topics/${topicId}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (res.status === 409) {
    throw await readAlreadyUsed(
      res,
      'Este tópico tem perguntas que já foram usadas para avaliar alguém.',
    )
  }
  await parseOrThrow<void>(res, 'delete topic')
}

export async function reorderTopics(
  assessmentId: string,
  items: ReorderTopicItem[],
): Promise<InterviewTopic[]> {
  const res = await fetch(`${API_BASE}/assessments/${assessmentId}/topics/reorder`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(items),
  })
  return parseOrThrow<InterviewTopic[]>(res, 'reorder topics')
}

/**
 * GET /api/assessments/:id — owned by Story 2.1. The response shape
 * includes `topics` nested per architecture.md §7.2. We only consume
 * the `topics` slice in Story 2.4.
 */
export async function getAssessmentTopics(assessmentId: string): Promise<InterviewTopic[]> {
  const res = await fetch(`${API_BASE}/assessments/${assessmentId}/topics`, {
    credentials: 'include',
  })
  return parseOrThrow<InterviewTopic[]>(res, 'fetch topics')
}

// ============================================================
// Story 2.5 — Questions CRUD + reorder
// Endpoints (architecture.md §7.2):
//   POST   /api/assessments/:id/questions
//   PATCH  /api/assessments/:id/questions/:questionId
//   DELETE /api/assessments/:id/questions/:questionId
//   PATCH  /api/assessments/:id/questions/reorder
//   GET    /api/assessments/:id/questions
// ============================================================

export interface CreateQuestionInput {
  topicId: string
  canonicalText: string
  // O `questionNumber` NÃO entra aqui: quem o deriva da posição do tópico e o
  // grava é o servidor (ver `packages/shared/src/numbering.ts`). Enquanto o
  // cliente o mandava, dois tópicos com a mesma inicial produziam o mesmo
  // código — e o código é a chave com que o LLM devolve as detecções.
  /**
   * Initial rubric — REQUIRED by the backend (min 2 levels with unique scores).
   * Use `DEFAULT_RUBRIC` from `lib/question-defaults.ts` when creating from a
   * generic "+ Nova pergunta" affordance; users edit it in the RubricEditor afterwards.
   */
  rubric: RubricLevel[]
  /** Server assigns when omitted (`MAX(order) + 1` per architecture). */
  order?: number
  weight?: number
  evaluatorNotes?: string | null
}

export interface UpdateQuestionInput {
  /** Move between topics (AC8). Server recomputes destination order = MAX + 1. */
  topicId?: string
  canonicalText?: string
  evaluatorNotes?: string | null
  weight?: number
  order?: number
  /**
   * Full rubric matrix (Story 2.6 — AC14). Sent ascending by score; the
   * Worker validates against the shared `RubricSchema` before persisting
   * to the `rubric` JSON column (architecture §5.1 / §7.3).
   */
  rubric?: RubricLevel[]
}

export interface ReorderQuestionItem {
  /** Canonical `id` per REQ-001 fix landed in Story 2.4. */
  id: string
  order: number
}

export async function getAssessmentQuestions(assessmentId: string): Promise<InterviewQuestion[]> {
  const res = await fetch(`${API_BASE}/assessments/${assessmentId}/questions`, {
    credentials: 'include',
  })
  return parseOrThrow<InterviewQuestion[]>(res, 'fetch questions')
}

export async function createQuestion(
  assessmentId: string,
  input: CreateQuestionInput,
): Promise<InterviewQuestion> {
  const res = await fetch(`${API_BASE}/assessments/${assessmentId}/questions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  })
  return parseOrThrow<InterviewQuestion>(res, 'create question')
}

export async function updateQuestion(
  assessmentId: string,
  questionId: string,
  input: UpdateQuestionInput,
): Promise<InterviewQuestion> {
  const res = await fetch(`${API_BASE}/assessments/${assessmentId}/questions/${questionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  })
  return parseOrThrow<InterviewQuestion>(res, 'update question')
}

export async function deleteQuestion(assessmentId: string, questionId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/assessments/${assessmentId}/questions/${questionId}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (res.status === 409) {
    throw await readAlreadyUsed(res, 'Esta pergunta já foi usada para avaliar alguém.')
  }
  await parseOrThrow<void>(res, 'delete question')
}

/**
 * `Promise<void>` e não `Promise<InterviewQuestion[]>`: a rota responde **204
 * sem corpo**, e o `parseOrThrow` devolve `undefined` nesse caso. O tipo antigo
 * dizia que vinha uma lista — nunca vinha, e ninguém deu por isso porque este
 * cliente esteve sem chamador nenhum até o arrasto de perguntas o ligar.
 */
export async function reorderQuestions(
  assessmentId: string,
  items: ReorderQuestionItem[],
): Promise<void> {
  const res = await fetch(`${API_BASE}/assessments/${assessmentId}/questions/reorder`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(items),
  })
  await parseOrThrow<void>(res, 'reorder questions')
}

export { AssessmentsApiError }

// ---------------------------------------------------------------
// Rubrica comportamental (migration 0019)
//
// Rubrica editável de engajamento/colaboração/comunicação aplicada a toda a
// entrevista. Endpoints:
//   GET   /api/assessments/:id/behavioral-config  → rubrica EFETIVA + isDefault
//   PATCH /api/assessments/:id/behavioral-config  → replace completo (admin)
//
// O GET nunca devolve vazio: quando o assessment não tem rubrica própria, a API
// resolve para a padrão e marca
// `isDefault: true` — daí o editor abrir já preenchido.
// ---------------------------------------------------------------

/** Resposta de `GET /api/assessments/:id/behavioral-config`. */
export interface BehavioralConfigResponse {
  behavioralConfig: BehavioralConfig
  /** `true` = ainda é a rubrica padrão herdada, nada foi gravado neste assessment. */
  isDefault: boolean
}

export async function getBehavioralConfig(
  assessmentId: string,
  signal?: AbortSignal,
): Promise<BehavioralConfigResponse> {
  const init: RequestInit = { credentials: 'include', headers: { Accept: 'application/json' } }
  if (signal) init.signal = signal
  const res = await fetch(
    `${API_BASE}/assessments/${encodeURIComponent(assessmentId)}/behavioral-config`,
    init,
  )
  if (!res.ok) throw new Error(`Failed to fetch behavioral config (${res.status})`)
  const json = (await res.json()) as {
    assessment: { behavioralConfig: BehavioralConfig; isDefault: boolean }
  }
  return {
    behavioralConfig: json.assessment.behavioralConfig,
    isDefault: json.assessment.isDefault,
  }
}

export async function updateBehavioralConfig(
  assessmentId: string,
  config: BehavioralConfig,
): Promise<BehavioralConfig> {
  const res = await fetch(
    `${API_BASE}/assessments/${encodeURIComponent(assessmentId)}/behavioral-config`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(config),
    },
  )
  if (!res.ok) throw new Error(`Failed to update behavioral config (${res.status})`)
  const json = (await res.json()) as { assessment: { behavioralConfig: BehavioralConfig } }
  return json.assessment.behavioralConfig
}

// ---------------------------------------------------------------
// Duplicar um modelo — 2026-09-02
//
// Endpoint: POST /api/assessments/:id/duplicate  → a cópia (201)
//
// Esta secção era a do EXPORT (`GET /:idOrSlug/export`, Story 2.8), removido
// junto com o item "Exportar JSON" da listagem, que era o seu único chamador.
// O que ficou por resolver era o motivo real de quem exportava: começar um
// modelo novo a partir de um que já existe. Duplicar resolve isso sem passar
// pelo arquivo — e sem passar pelo envelope de export, que não carrega a
// rubrica comportamental nem a configuração de correção (ver o comentário da
// rota no Worker).
//
// A contraparte `POST /api/assessments/import` continua no Worker e continua
// sem chamador no web, desde 2026-07-28 (decisão do owner).
// ---------------------------------------------------------------

/**
 * POST /api/assessments/:id/duplicate — cria uma cópia do modelo.
 *
 * O corpo leva só o nome: o slug é derivado dele no servidor e uniquificado
 * lá (`copia-de-x`, `copia-de-x-2`, …), portanto duplicar duas vezes o mesmo
 * modelo nunca devolve 409 por colisão — só se o nome esgotar os sufixos.
 *
 * Devolve a cópia já com tópicos e perguntas, no mesmo formato do import. Quem
 * chama só precisa do `id` para navegar — daí o retorno estar tipado como
 * `Assessment` e não como o envelope inteiro.
 *
 * ⚠️ A resposta trazia TAMBÉM `asrGlossary` até 2026-09-05, e o diálogo que
 * anuncia a duplicação prometia o glossário ao usuário. As duas coisas saíram
 * na mesma leva que o glossário ASR (ver a lápide no cabeçalho do arquivo): a
 * cópia leva tópicos, perguntas e rubrica, e é isso que a frase do diálogo diz
 * agora. Se alguém voltar a acrescentar uma coleção ao duplicar, a frase do
 * `duplicate-assessment-dialog.tsx` é parte da mudança, não uma nota de rodapé.
 */
export async function duplicateAssessment(id: string, name: string): Promise<Assessment> {
  const res = await fetch(`${API_BASE}/assessments/${encodeURIComponent(id)}/duplicate`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ name }),
  })
  if (!res.ok) {
    // `readProblemMessage` e NÃO o `errorMessage` deste arquivo: o segundo lê um
    // envelope `{ error }` que esta API não produz, e por isso engoliria as duas
    // frases que aqui importam — o 409 de sufixos esgotados e o 400 de nome sem
    // slug, ambos em `detail` (RFC 7807). Os dois resolvem-se mudando o nome, e
    // a tela precisa de os poder mostrar tal como o servidor os escreveu.
    throw new ApiError(
      await readProblemMessage(res, 'Não foi possível duplicar o modelo. Tente novamente.'),
      res.status,
      null,
    )
  }
  return (await res.json()) as Assessment
}
